// ─── Template / Configuration ────────────────────────────────────────────────

export interface TemplateColumn {
  key: string;
  /** Display header used in the UI and for auto-matching */
  header: string;
  /** Field is required – import fails if absent */
  required?: boolean;
  /** One or more alternate header names for auto-matching */
  aliases?: string[];
  /** Regex pattern the cell value must satisfy */
  pattern?: string;
  /** Min / max numeric bounds */
  min?: number;
  max?: number;
  /** Supported enum values – value must be one of these */
  enumValues?: string[];
  /** Custom Zod validator (value) => z.SafeParseReturnType */
  customValidator?: string; // stored as serialized Zod schema string
}

export interface TemplateSheet {
  /** Sheet name as it appears in the template config */
  name: string;
  /** If true and no matching workbook sheet is found → error */
  required?: boolean;
  /** Column definitions for this sheet */
  columns: TemplateColumn[];
  /** Allow rows that don't match any template column key */
  allowExtraColumns?: boolean;
}

export interface ExcelTemplate {
  id: string;
  /** Human-readable name shown in the UI */
  label: string;
  sheets: TemplateSheet[];
  /** If true, raw formulas (=A2+B2) are preserved instead of computed values */
  preserveFormula?: boolean;
  /** Called with all rows after mapping; must throw or return rows unchanged */
  customValidator?: string;
}

// ─── Workbook / Sheet Data ────────────────────────────────────────────────────

/** Raw cell value as read from xlsx */
export type RawCellValue = string | number | boolean | null | undefined;

/** A single row of raw cell values keyed by column header */
export type RawRow = Record<string, RawCellValue>;

/**
 * Provenance keys carried on every parsed row so a bound value can be traced
 * back to its Excel origin. They are never exposed as selectable columns.
 */
export const ROW_SHEET_KEY = "__sheet";
export const ROW_INDEX_KEY = "__rowIndex";
export const ROW_META_KEYS: readonly string[] = [ROW_SHEET_KEY, ROW_INDEX_KEY];

export function isRowMetaKey(key: string): boolean {
  return ROW_META_KEYS.includes(key);
}

export interface SheetData {
  name: string;
  /** Column headers in display order */
  headers: string[];
  /** Raw rows (unmapped) */
  rows: RawRow[];
  /** 1-based row index → error message for rows that failed to parse */
  rowErrors?: Record<number, string>;
  hidden?: boolean;
  passwordProtected?: boolean;
}

export interface ParsedWorkbook {
  name: string;
  sheets: SheetData[];
  meta: WorkbookMeta;
}

export interface WorkbookMeta {
  sheetCount: number;
  totalRows: number;
  author?: string;
  created?: string;
  modified?: string;
}

// ─── Column Mapping ───────────────────────────────────────────────────────────

/** A single column's mapping decision */
export interface ColumnMapping {
  /** Template column key */
  templateKey: string;
  /** Workbook column header this maps to – undefined = unmapped */
  workbookHeader?: string;
  /** How the mapping was resolved */
  source: "auto" | "manual" | "unmapped";
}

/** Mapping result for an entire sheet */
export interface SheetMapping {
  templateSheetName: string;
  mappings: ColumnMapping[];
  /** Workbook sheet name this was matched to */
  workbookSheetName?: string;
}

// ─── Validation ───────────────────────────────────────────────────────────────

export type ValidationSeverity = "error" | "warning" | "info";

export interface ValidationIssue {
  rowIndex: number; // 0-based
  columnKey: string;
  value: RawCellValue;
  severity: ValidationSeverity;
  message: string;
  /** Short machine-readable code for i18n lookups */
  code: string;
}

export interface ValidationResult {
  /** All rows that passed validation */
  validRows: ImportRow[];
  /** All rows that failed (error severity) */
  invalidRows: ImportRow[];
  /** Rows with warnings only (still importable) */
  warningRows: ImportRow[];
  /** Flat list of all issues */
  issues: ValidationIssue[];
  /** Summary counts */
  stats: ValidationStats;
}

export interface ValidationStats {
  totalRows: number;
  validCount: number;
  errorCount: number;
  warningCount: number;
}

// ─── Import Row ───────────────────────────────────────────────────────────────

export interface ImportRow {
  /** 0-based index in the original workbook */
  rowIndex: number;
  /** Mapped values keyed by template column key */
  data: Record<string, RawCellValue>;
  /** Parsed/transformed values */
  parsed: Record<string, unknown>;
  /** Validation issues for this row */
  issues: ValidationIssue[];
  /** Overall status */
  status: "valid" | "warning" | "error";
}

// ─── Preview ─────────────────────────────────────────────────────────────────

export interface PreviewColumn {
  key: string;
  header: string;
  /** Original workbook value */
  original: RawCellValue;
  /** Transformed value */
  transformed: unknown;
  /** Validation issue if any */
  issue?: ValidationIssue;
}

export interface PreviewRow {
  rowIndex: number;
  columns: PreviewColumn[];
  status: "valid" | "warning" | "error";
  issues: ValidationIssue[];
}

// ─── Import API ───────────────────────────────────────────────────────────────

export interface ImportPayload {
  templateId: string;
  sheet: string;
  rows: ImportRow["data"][];
}

export interface ImportResponse {
  success: boolean;
  importedCount: number;
  errors?: Array<{ rowIndex: number; message: string }>;
}

// ─── Mapping Profiles ─────────────────────────────────────────────────────────

export interface MappingProfile {
  id: string;
  templateId: string;
  templateSheetName: string;
  profileName: string;
  /** workbookHeader → templateKey */
  mappings: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

// ─── Wizard State ─────────────────────────────────────────────────────────────

export type WizardStep =
  | "upload"
  | "sheet"
  | "mapping"
  | "validate"
  | "preview"
  | "import";

export interface WizardState {
  step: WizardStep;
  file: File | null;
  workbook: ParsedWorkbook | null;
  selectedTemplate: ExcelTemplate | null;
  sheetMappings: SheetMapping[];
  validation: ValidationResult | null;
  importing: boolean;
  importResult: ImportResponse | null;
  error: string | null;
}

// ─── Parser Config ─────────────────────────────────────────────────────────────

export interface ParserConfig {
  /** Pass true to get raw formula strings instead of computed values */
  preserveFormula?: boolean;
  /** Number of rows to sample for header detection (default 10) */
  headerSampleSize?: number;
  /** Sheet name to default to (first sheet if omitted) */
  defaultSheet?: string;
  /** Called with progress 0-100 during large file parsing */
  onProgress?: (pct: number) => void;
}

// ─── Error Types ──────────────────────────────────────────────────────────────

export class WorkbookParseError extends Error {
  constructor(
    message: string,
    public readonly code:
      | "FILE_TOO_LARGE"
      | "INVALID_FORMAT"
      | "PASSWORD_PROTECTED"
      | "NO_SHEETS"
      | "HIDDEN_SHEET"
      | "PARSE_ERROR",
    public readonly sheet?: string,
  ) {
    super(message);
    this.name = "WorkbookParseError";
  }
}

export class ValidationError extends Error {
  constructor(
    message: string,
    public readonly issues: ValidationIssue[],
  ) {
    super(message);
    this.name = "ValidationError";
  }
}